/**
 * OwnerDashboard — Task 4.2
 * Portfolio view for owners: all owned centres, totals, pending payouts, centre switch.
 * READ-ONLY — no payout approval, no MRR display (PlanConfig.monthlyPriceINR not yet seeded).
 * ADR-007: owner = portfolio layer only. Operational access via separate centreAdmin account.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWindowSize } from '../utils/useWindowSize';
import { getOwnerSummary, getOwnerPayouts, switchCentre } from '../utils/api';
import OwnerPayoutCard from '../components/OwnerPayoutCard';

const PLAN_COLOURS = { FREE: { bg: '#f3f4f6', text: '#6b7280' }, STARTER: { bg: '#eff6ff', text: '#3b82f6' }, AI_ADMIN: { bg: '#f0fdf4', text: '#16a34a' }, SCALE: { bg: '#faf5ff', text: '#9333ea' } };
const SUB_COLOURS  = { active: { bg: '#f0fdf4', text: '#16a34a' }, halted: { bg: '#fef9c3', text: '#b45309' }, cancelled: { bg: '#fef2f2', text: '#dc2626' }, authenticated: { bg: '#eff6ff', text: '#3b82f6' } };

export default function OwnerDashboard() {
  const { user, logout }  = useAuth();
  const { isMobile }      = useWindowSize();
  const navigate          = useNavigate();

  const [summary,       setSummary]       = useState(null);
  const [payouts,       setPayouts]       = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [switching,     setSwitching]     = useState(null); // tenantId being switched to

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Promise.allSettled: if one fetch fails, the other still renders partial data
      const [sRes, pRes] = await Promise.allSettled([getOwnerSummary(), getOwnerPayouts()]);
      if (sRes.status === 'fulfilled') setSummary(sRes.value.data.data);
      if (pRes.status === 'fulfilled') setPayouts(pRes.value.data.data);
      if (sRes.status === 'rejected' && pRes.status === 'rejected') {
        setError('Failed to load dashboard. Please refresh.');
      }
    } catch {
      setError('Failed to load dashboard. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSwitch(tenantId) {
    setSwitching(tenantId);
    try {
      const res = await switchCentre(tenantId);
      const activeId = res.data?.data?.activeTenantId;
      if (!activeId) throw new Error('Invalid switch response');
      sessionStorage.setItem('activeTenantId', activeId);
      // ADR-007: owner stays on portfolio view — centreAdmin access via separate admin account
      navigate('/owner');
    } catch {
      setError('Centre switch failed. Please try again.');
      setSwitching(null);
    }
  }

  const totals  = summary?.totals;
  const centres = summary?.centres || [];
  const pad     = isMobile ? '16px' : '32px';

  if (loading) return <div style={s.loading}>Loading your dashboard…</div>;
  if (error)   return <div style={s.errorPage}>{error}</div>;

  return (
    <div style={s.layout}>
      {/* ── Header ── */}
      <div style={s.topbar}>
        <div style={{ ...s.topbarInner, padding: `0 ${pad}` }}>
          <div style={s.logoArea}>
            <div style={s.logoText}>LifeChiAI</div>
            <div style={s.logoSub}>Owner Dashboard</div>
          </div>
          <div style={s.topbarRight}>
            <span style={s.userName}>{user?.firstName} {user?.lastName}</span>
            <button style={s.logoutBtn} onClick={logout}>Logout</button>
          </div>
        </div>
      </div>

      <div style={{ padding: `24px ${pad}`, maxWidth: '1100px', margin: '0 auto' }}>

        {/* ── Section 2: Totals strip ── */}
        <div style={{ ...s.statsGrid, gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr' }}>
          {[
            { label: 'Centres',  value: totals?.centres  ?? 0 },
            { label: 'Patients', value: totals?.patients ?? 0 },
            { label: 'Sessions', value: totals?.sessions ?? 0 },
            { label: 'Healers',  value: totals?.healers  ?? 0 }
          ].map(stat => (
            <div key={stat.label} style={s.statCard}>
              <div style={s.statNum}>{stat.value.toLocaleString('en-IN')}</div>
              <div style={s.statLabel}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* ── Section 3: Centre cards ── */}
        <div style={s.sectionTitle}>Your Centres</div>
        {centres.length === 0 && <div style={s.empty}>No centres found in your account.</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '32px' }}>
          {centres.map(c => {
            const planCol = PLAN_COLOURS[c.plan]  || PLAN_COLOURS.FREE;
            const subCol  = SUB_COLOURS[c.subscriptionStatus] || { bg: '#f3f4f6', text: '#6b7280' };
            return (
              <div key={c.tenantId} style={s.centreCard}>
                <div style={{ ...s.centreCardInner, flexDirection: isMobile ? 'column' : 'row' }}>
                  <div style={s.centreInfo}>
                    <div style={s.centreName}>{c.tenantName}</div>
                    <div style={s.centreCity}>{c.city}</div>
                    <div style={s.badgeRow}>
                      <span style={{ ...s.planBadge, background: planCol.bg, color: planCol.text }}>{c.plan}</span>
                      {c.subscriptionStatus && (
                        <span style={{ ...s.planBadge, background: subCol.bg, color: subCol.text }}>
                          {c.subscriptionStatus}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ ...s.centreStats, flexDirection: isMobile ? 'row' : 'row', justifyContent: isMobile ? 'flex-start' : 'center' }}>
                    {[
                      { label: 'Patients', value: c.patientCount },
                      { label: 'Sessions', value: c.sessionCount },
                      { label: 'Healers',  value: c.healerCount  }
                    ].map(st => (
                      <div key={st.label} style={s.miniStat}>
                        <div style={s.miniNum}>{st.value}</div>
                        <div style={s.miniLabel}>{st.label}</div>
                      </div>
                    ))}
                  </div>
                  <button
                    style={{ ...s.switchBtn, opacity: switching === c.tenantId ? 0.6 : 1 }}
                    onClick={() => handleSwitch(c.tenantId)}
                    disabled={!!switching}
                  >
                    {switching === c.tenantId ? 'Switching…' : 'Switch to this Centre'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Billing & Plan ── */}
        <div style={s.sectionTitle}>Billing & Plan</div>
        <div style={s.billingCard}>
          <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#555' }}>
            View and manage your LifeChiAI subscription.
          </p>
          <button style={s.billingBtn} onClick={() => navigate('/owner/billing')}>
            Manage Plan →
          </button>
        </div>

        {/* ── Section 4: Pending payouts ── */}
        <div style={s.sectionTitle}>
          Pending Payouts
          {payouts?.totalPendingPayouts > 0 && (
            <span style={s.pendingCount}>{payouts.totalPendingPayouts} pending</span>
          )}
        </div>
        {(!payouts?.centres || payouts.centres.length === 0)
          ? <div style={s.empty}>No pending payouts across your centres.</div>
          : payouts.centres.map(c => <OwnerPayoutCard key={c.tenantId} centre={c} />)
        }
      </div>
    </div>
  );
}

const BRAND_GREEN = '#4a8f82';

const RAL = "'Raleway', sans-serif";
const s = {
  layout:      { minHeight: '100vh', background: '#f5f8f5', fontFamily: "'Open Sans', sans-serif" },
  topbar:      { background: 'linear-gradient(135deg, #071e12, #0c2617)', borderBottom: `3px solid ${BRAND_GREEN}` },
  topbarInner: { maxWidth: '1100px', margin: '0 auto', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logoText:    { fontFamily: RAL, fontWeight: '700', fontSize: '18px', color: '#fff' },
  logoSub:     { fontSize: '10px', color: '#8DC63F', textTransform: 'uppercase', letterSpacing: '1.5px', marginTop: '2px' },
  topbarRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  userName:    { fontSize: '13px', color: 'rgba(255,255,255,0.7)' },
  logoutBtn:   { padding: '8px 20px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', fontFamily: RAL, fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', minHeight: '44px' },
  statsGrid:   { display: 'grid', gap: '16px', marginBottom: '32px' },
  statCard:    { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '24px', textAlign: 'center' },
  statNum:     { fontFamily: RAL, fontWeight: '700', fontSize: '32px', color: BRAND_GREEN, lineHeight: 1 },
  statLabel:   { fontSize: '11px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '6px' },
  sectionTitle:{ fontFamily: RAL, fontWeight: '700', fontSize: '15px', color: '#1a1a2e', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' },
  pendingCount:{ background: '#fef3e2', color: '#d97706', fontFamily: RAL, fontWeight: '700', fontSize: '11px', padding: '3px 10px', borderRadius: '20px' },
  empty:       { color: '#999', fontSize: '13px', padding: '20px 0' },
  centreCard:  { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' },
  centreCardInner: { display: 'flex', alignItems: 'center', padding: '20px', gap: '20px', flexWrap: 'wrap' },
  centreInfo:  { flex: 1, minWidth: '180px' },
  centreName:  { fontFamily: RAL, fontWeight: '700', fontSize: '15px', color: '#1a1a2e', marginBottom: '2px' },
  centreCity:  { fontSize: '12px', color: '#888', marginBottom: '8px' },
  badgeRow:    { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  planBadge:   { fontFamily: RAL, fontWeight: '700', fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', padding: '3px 10px', borderRadius: '20px' },
  centreStats: { display: 'flex', gap: '20px' },
  miniStat:    { textAlign: 'center' },
  miniNum:     { fontFamily: RAL, fontWeight: '700', fontSize: '20px', color: BRAND_GREEN, lineHeight: 1 },
  miniLabel:   { fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '3px' },
  switchBtn:   { background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: '6px', padding: '12px 20px', fontFamily: RAL, fontWeight: '700', fontSize: '12px', letterSpacing: '0.5px', textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap', minHeight: '44px' },
  billingCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px', marginBottom: '32px' },
  billingBtn:  { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' },
  loading:     { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#666', fontSize: '14px' },
  errorPage:   { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#dc2626', fontSize: '14px' }
};
